import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles } from './articleData';
import type { ArticleData } from './articleData';

function buildArticleJsonLd(article: ArticleData): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.metaDesc,
        image: article.heroImage,
        datePublished: '2026-07-20',
        dateModified: '2026-07-21',
        author: {
          '@type': 'Organization',
          name: 'Kakobuy Polska Spreadsheet Hub',
          url: 'https://kakobuypolska.study',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Kakobuy Polska Spreadsheet Hub',
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://kakobuypolska.study/artykul/${article.slug}`,
        },
        inLanguage: 'pl-PL',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Strona główna',
            item: 'https://kakobuypolska.study',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.title,
            item: `https://kakobuypolska.study/artykul/${article.slug}`,
          },
        ],
      },
    ],
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article: ArticleData | undefined = slug ? articles[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="w-full bg-background-50 min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3 font-heading">Artykuł nie został znaleziony</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-semibold cursor-pointer">
              ← Wróć do strony głównej
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = buildArticleJsonLd(article);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="w-full bg-background-50">
        <div className="w-full h-48 md:h-64 overflow-hidden">
          <img
            src={article.heroImage}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <header className="mb-8 md:mb-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">Home</Link>
              <span>/</span>
              <span className="text-foreground-800">{article.categoryTag}</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3 font-heading leading-tight">
              {article.title}
            </h1>
            <p className="text-sm text-foreground-600">
              Opublikowano: {article.date} • Czas czytania: {article.readTime}
            </p>
          </header>

          <div className="prose-custom text-foreground-800 leading-relaxed space-y-6 text-sm md:text-base">
            {article.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-heading">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pIdx) => (
                  <p key={pIdx} className={pIdx < section.paragraphs.length - 1 ? 'mb-3' : ''}>
                    {para}
                  </p>
                ))}
              </section>
            ))}

            <section className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-3 font-heading">
                Podsumowanie
              </h2>
              <p>
                Mamy nadzieję, że ten artykuł pomógł Ci lepiej zrozumieć świat zakupów przez Kakobuy. Pamiętaj, że regularnie aktualizujemy nasze treści i dodajemy nowe artykuły – sprawdzaj naszą <Link to="/" className="text-primary-500 hover:text-primary-600 font-semibold cursor-pointer">stronę główną</Link>, aby być na bieżąco z najnowszymi poradnikami i znaleziskami w Kakobuy Spreadsheet.
              </p>
              <p className="mb-0">
                Jeśli masz pytania lub szukasz konkretnych produktów, zawsze możesz skorzystać z <strong>Kakobuy Spreadsheet</strong> – naszego arkusza z setkami zweryfikowanych linków do najlepszych produktów z Chin.
              </p>
            </section>
          </div>

          <div className="mt-8 md:mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-600 mb-2">Przydatne linki:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/" className="whitespace-nowrap cursor-pointer text-xs bg-background-100 border border-background-200/70 px-3 py-1.5 rounded-full text-foreground-700 hover:text-foreground-950 hover:border-background-300/60 transition-all">
                    ← Strona główna
                  </Link>
                  <Link to="/artykul/kakobuy-dla-poczatkujacych" className="whitespace-nowrap cursor-pointer text-xs bg-background-100 border border-background-200/70 px-3 py-1.5 rounded-full text-foreground-700 hover:text-foreground-950 hover:border-background-300/60 transition-all">
                    Poradnik dla początkujących
                  </Link>
                  <Link to="/artykul/kakobuy-jak-kupowac-bezpiecznie" className="whitespace-nowrap cursor-pointer text-xs bg-background-100 border border-background-200/70 px-3 py-1.5 rounded-full text-foreground-700 hover:text-foreground-950 hover:border-background-300/60 transition-all">
                    Bezpieczeństwo zakupów
                  </Link>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="whitespace-nowrap cursor-pointer text-xs bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full hover:bg-primary-200 transition-all">
                    Kakobuy Spreadsheet
                  </a>
                </div>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-all duration-200"
              >
                <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
                Sprawdź Kakobuy Spreadsheet
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}